import React, { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { EmblaOptionsType } from "embla-carousel";

import {
    BULLION,
    BULLION_KEY,
    BULLION_PRODUCT,
    BULLION_SIP_TYPE_KEY,
    // BullionInvestmentType,
    // BullionSIPType,
    LatestDetails,
    PRODUCT_CURRENCY /*, PRODUCT_WEIGHT */,
    PRODUCT_WEIGHT,
    ProductDataResponse,
} from "@/app/bullion/type-constants";
import useMobile from "@/app/hooks/use-mobile";
import { formatIndianCurrency } from "@/app/utils/helper";
import { getBullionPrices } from "@/app/api/service/bullion";
import { Input } from "@/components/ui/input";
import LoadingIcon from "@/app/assets/icons/loading";
import BullionCarousel from "./bullion-carousel";

interface Props {
    type?: BULLION_KEY;
    setProductsData: (res: ProductDataResponse) => void;
}

const SIP = {
    DAILY: {
        MIN_AMT: 10,
        MAX_AMT: 5000,
        LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
    },
    WEEKLY: {
        MIN_AMT: 10,
        MAX_AMT: 5000,
        LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
    },
    MONTHLY: {
        MIN_AMT: 10,
        MAX_AMT: 15000,
        LAST_FETCHED: { AMOUNT: 0, WEIGHT: 0 },
    },
};
const deBounceTimeout: number = 1000;
const OPTIONS: EmblaOptionsType = { axis: "y", loop: true };
const SLIDES = [
    "Start investing with as little as Rs.10/-",
    "Positive return in 9 out of 10yrs",
    "Value increase by 204% in 8yrs",
];

const SIPCalc: FC<Props> = memo(function SIPCalc({ type, setProductsData }) {
    const { isMobile } = useMobile();
    const TYPE = type ?? BULLION_PRODUCT.GOLD.TYPE;
    const tabs = ["Daily", "Weekly", "Monthly"];
    const [activeTab, setActiveTab] = useState<BULLION_SIP_TYPE_KEY>(
        tabs[0].toUpperCase() as BULLION_SIP_TYPE_KEY,
    );
    const [priceValue, setPriceValue] = useState<string>(
        String(SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MIN_AMT),
    );
    const [priceDisplayValue, setPriceDisplayValue] = useState<string>(
        formatIndianCurrency(Number(priceValue)),
    );
    const [activeField, setActiveField] = useState<string>("price");
    const [quantity, setQuantity] = useState<string>("1");
    const [displayQuantity, setDisplayQuantity] = useState<string>(quantity + PRODUCT_WEIGHT.GRAM);
    const [latestDetails, setLatestDetails] = useState<LatestDetails | null>(null);
    const [isPriceFetching, setIsPriceFetching] = useState(false);
    const [hasError, setHasError] = useState(false);

    const refPriceSIP = useRef<HTMLInputElement>(null);
    const refGmSIP = useRef<HTMLInputElement>(null);

    function debounce<T extends (...args: (string | number | boolean)[]) => void>(
        func: T,
        delay: number,
        noExecute?: boolean,
    ): (...args: Parameters<T>) => void {
        let timeoutId: ReturnType<typeof setTimeout>;
        return (...args: Parameters<T>) => {
            clearTimeout(timeoutId);
            if (!noExecute) {
                timeoutId = setTimeout(() => {
                    func(...args);
                }, delay);
            }
        };
    }

    const validateFields = (value: number | string, parsedValue: number) => {
        setHasError(false);
        if (activeField === "price" || activeField === "") {
            if (
                typeof parsedValue != "number" ||
                parsedValue == Number.NEGATIVE_INFINITY ||
                parsedValue == Number.POSITIVE_INFINITY ||
                parsedValue < SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MIN_AMT
            ) {
                setPriceValue(String(value));
                setQuantity("0");
                setHasError(true);
                debouncedLog(value, activeField, true);
            } else if (parsedValue > SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MAX_AMT) {
                setPriceValue(String(value));
                setQuantity("0");
                setHasError(true);
                debouncedLog(value, activeField, true);
            } else {
                setPriceValue(String(value));
                setQuantity((item) => item);
                setHasError(false);
                if (!activeField) {
                    debouncedLog(value, "price");
                } else {
                    debouncedLog(value, activeField);
                }
            }
        } else if (activeField === "gm") {
            if (
                typeof parsedValue != "number" ||
                parsedValue == Number.NEGATIVE_INFINITY ||
                parsedValue == Number.POSITIVE_INFINITY ||
                parsedValue <= 0
            ) {
                setPriceValue("");
                setQuantity(String(value));
                setHasError(true);
                debouncedLog(value, activeField, true);
            } else {
                setQuantity(String(value));
                setHasError(false);
                debouncedLog(value, activeField);
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const parts = val.replace(/[^\d.]/g, "").split(".");
        let value = "";
        if (parts.length > 1) {
            value = parts.shift() + "." + parts.join("");
        } else {
            value = parts.join(".");
        }
        const parsedValue = parseFloat(value);

        validateFields(value, parsedValue);
    };

    const handleChangeOnBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const parsedValue = parseFloat(val);

        if (String(parsedValue) == "NaN" || (typeof parsedValue == "number" && parsedValue <= 0)) {
            setPriceValue("0");
            setPriceDisplayValue(formatIndianCurrency(Number(0)));
            setQuantity("0");
            setDisplayQuantity("0" + PRODUCT_WEIGHT.GRAM);
            setHasError(true);
        }

        setActiveField("");
    };

    const debouncedLog = useCallback(
        debounce((value, unit, noExecute = false) => {
            if (
                typeof value === "string" &&
                (parseFloat(value) >=
                    SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MIN_AMT ||
                    parseFloat(value) <=
                        SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MAX_AMT)
            ) {
                fnGetBullionPrices(String(value), String(unit), Boolean(noExecute));
            }
        }, deBounceTimeout),
        [],
    );

    const fnGetBullionPrices = async (value?: string, unit?: string, noExecute?: boolean) => {
        if (!noExecute) {
            setIsPriceFetching(true);

            await getBullionPrices({
                product_code: BULLION[TYPE].CODE, //BULLION_PRODUCT[type ?? BULLION_PRODUCT.GOLD.TYPE].CODE,
                quantity: !unit || unit == "undefined" ? quantity : value,
                time_frame: "1Y",
                unit_type:
                    !unit || unit == "undefined"
                        ? BULLION_PRODUCT[TYPE].PRICE_SYMBOL
                        : unit === "price"
                          ? BULLION_PRODUCT[TYPE].PRICE_SYMBOL
                          : BULLION_PRODUCT[TYPE].WEIGHT_SYMBOL,
            })
                .then((response) => {
                    if (response.success && response.data.latest_details) {
                        setProductsData(response);
                        setLatestDetails(response.data.latest_details);
                        if (typeof window != "undefined") {
                            window.focus();
                        }
                    } else {
                        console.error("No latest details found in response");
                        setLatestDetails(null);
                    }
                    setIsPriceFetching(false);
                })
                .catch((error) => {
                    setIsPriceFetching(false);
                    console.error("Error fetching bullion prices: ", error);
                    throw error;
                });
        }
    };

    /* useEffect(() => {
        debugger;
        if (
            BULLION[TYPE].LAST_FETCHED.AMOUNT <
            SIP[
                activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY
            ].MIN_AMT
        ) {
            fnGetBullionPrices();
        } else {
            setPriceValue(String(BULLION[TYPE].LAST_FETCHED.AMOUNT));
            setPriceDisplayValue(formatIndianCurrency(Number(BULLION[TYPE].LAST_FETCHED.AMOUNT)));
            setQuantity(String(BULLION[TYPE].LAST_FETCHED.WEIGHT));
            setDisplayQuantity(String(BULLION[TYPE].LAST_FETCHED.WEIGHT) + PRODUCT_WEIGHT.GRAM);
        }
    }, [TYPE]); */

    useEffect(() => {
        fnGetBullionPrices(
            String(SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MIN_AMT),
            "price",
        );
    }, []);

    useEffect(() => {
        if (latestDetails?.amount && latestDetails?.grams) {
            const price = latestDetails.amount || 10;
            const weight = latestDetails.grams || 0;
    
            SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].LAST_FETCHED = {
                AMOUNT: price,
                WEIGHT: weight,
            };
    
            setPriceValue(String(price));
            setQuantity(String(weight));
            setIsPriceFetching(false);
    
            if (
                price < SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MIN_AMT ||
                price > SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MAX_AMT
            ) {
                setHasError(true);
            } else {
                setHasError(false);
            }
        }
    }, [latestDetails?.amount, latestDetails?.grams]);
    
    useEffect(() => {
        setPriceDisplayValue(formatIndianCurrency(Number(priceValue)));
        setDisplayQuantity(quantity + PRODUCT_WEIGHT.GRAM);
    }, [priceValue, quantity]);

    const fnSetActiveTab = (e: React.MouseEvent, tab: BULLION_SIP_TYPE_KEY) => {
        // const element = e?.target as HTMLInputElement;
        // const value = element.id;
        setActiveTab(tab);
    };

    useEffect(() => {
        validateFields(priceValue, Number(priceValue));
    }, [activeTab]);

    return (
        <>
            <div className="flex flex-col px-s7 pb-s9 pt-3 md:px-s9 md:pb-8 md:pt-0">
                <div className="section-tabs sm:mt-2">
                    <ul className="broder-neutralOpacityDark-10 flex h-[54px] flex-row border-b text-center text-md font-semibold leading-[140%] text-neutral-300 md:h-[52px] [&>li.active]:border-b-[2px] [&>li.active]:border-brand-primary-500 [&>li.active]:text-brand-primary-500">
                        {tabs.map((item: string, index: number) => {
                            return (
                                <li
                                    key={index}
                                    id={"tab-" + item}
                                    tabIndex={0}
                                    className={
                                        "relative z-auto -mb-[0.5px] w-1/3 cursor-pointer px-s6 py-s7 " +
                                        (activeTab === item.toUpperCase() ? "active" : "")
                                    }
                                    onClick={(e) =>
                                        fnSetActiveTab(
                                            e,
                                            item.toUpperCase() as BULLION_SIP_TYPE_KEY,
                                        )
                                    }
                                >
                                    {item}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="pb-6 pt-8 md:py-s11">
                    {/* <div
                        id="block-calc"
                        className="relative flex min-h-[50px] items-center rounded-[12px] border border-neutral-100 md:mb-4 md:rounded-xl"
                    > */}
                    {/* <div
                            className={
                                "relative min-h-[46px] w-full rounded-xl border border-transparent pl-s7 " +
                                (activeField === "price"
                                    ? " " +
                                      "[&>span]:-top-[8px] [&>span]:bg-white [&>span]:text-sm [&>span]:opacity-100"
                                    : "")
                            }
                        >
                            <span className="absolute -top-[5px] left-[12px] bg-transparent px-1 text-sm leading-none opacity-0 transition-all duration-150 ease-in-out">{`${PRODUCT_CURRENCY[BULLION[BULLION.TYPE].CURRENCY]}`}</span>

                            <Input
                                ref={refPrice}
                                id="price"
                                name="price"
                                type={activeField === "price" && isMobile ? "number" : "text"}
                                value={activeField === "price" ? priceValue : priceDisplayValue}
                                onChange={(e) => {
                                    calculateValue(e.target);
                                }}
                                onFocus={(e) => {
                                    setActiveField(e.target.name?.trim());
                                }}
                                onBlur={() => {
                                    setActiveField("");
                                }}
                                className="h-[44px] !border-0 border-transparent p-0 text-left !text-base !font-regular !outline-none focus-visible:!ring-0 focus-visible:!ring-transparent focus-visible:!ring-offset-0"
                                spellCheck="false"
                                autoComplete="off"
                            />
                        </div> */}

                    <div
                        id="block-calc"
                        className={`relative flex min-h-[50px] items-center rounded-[12px] border md:mb-4 md:rounded-xl ${
                            isPriceFetching && (!priceValue || priceValue?.trim() == "0")
                                ? "border-error-600"
                                : "border-neutral-100"
                        }`}
                    >
                        <div
                            className={
                                "border-re relative min-h-[46px] rounded-xl border border-transparent pl-s7 " +
                                (activeField === "price"
                                    ? " " +
                                      "[&>span]:-top-[8px] [&>span]:bg-white [&>span]:text-sm [&>span]:opacity-100"
                                    : "")
                            }
                        >
                            <span className="absolute -top-[5px] left-[12px] bg-transparent px-1 text-sm leading-none opacity-0 transition-all duration-150 ease-in-out">{`${PRODUCT_CURRENCY[BULLION[BULLION.TYPE].CURRENCY]}`}</span>

                            <Input
                                ref={refPriceSIP}
                                id="price"
                                name="price"
                                type={activeField === "price" && isMobile ? "number" : "text"}
                                value={activeField === "price" ? priceValue : priceDisplayValue}
                                onChange={handleChange}
                                onFocus={(e) => {
                                    setActiveField(e.target.name);
                                }}
                                onBlur={handleChangeOnBlur}
                                className={
                                    "h-[44px] !border-0 border-transparent p-0 text-left !text-base !font-regular !outline-none focus-visible:!ring-0 focus-visible:!ring-transparent focus-visible:!ring-offset-0 " +
                                    (isPriceFetching ? "touch-none text-neutral-300" : "")
                                }
                                // disabled={isPriceFetching}
                                autoComplete="off"
                            />
                        </div>
                        <div
                            className={
                                "absolute left-[50%] top-[8px] h-[calc(100%-16px)] w-[1px] " +
                                (isPriceFetching ? "" : "bg-neutral-100")
                            }
                        >
                            {isPriceFetching && (
                                <div className="absolute left-[calc((100%-24px)/2)] top-[calc((100%-24px)/2)] h-6 w-6">
                                    <LoadingIcon className="m-auto h-6 w-6 animate-spin text-neutral-500" />
                                </div>
                            )}
                        </div>
                        <div
                            className={
                                "relative min-h-[46px] rounded-xl border border-transparent pr-s7 " +
                                (activeField === "gm"
                                    ? " " +
                                      "[&>span]:-top-[10px] [&>span]:bg-white [&>span]:text-sm [&>span]:opacity-100"
                                    : "")
                            }
                        >
                            <span className="absolute -top-[5px] right-[12px] bg-transparent px-1 text-sm leading-none opacity-0 transition-all duration-150 ease-in-out">{`${PRODUCT_WEIGHT[BULLION[BULLION.TYPE].UNIT]}`}</span>
                            <Input
                                ref={refGmSIP}
                                id="gm"
                                name="gm"
                                type={activeField === "gm" && isMobile ? "number" : "text"}
                                value={activeField === "gm" ? quantity : displayQuantity}
                                onChange={handleChange}
                                onFocus={(e) => {
                                    setActiveField(e.target.name);
                                }}
                                onBlur={handleChangeOnBlur}
                                className={
                                    "h-[44px] !border-0 border-transparent p-0 text-right !text-base !font-regular !outline-none focus-visible:!ring-0 focus-visible:!ring-transparent focus-visible:!ring-offset-0 " +
                                    (isPriceFetching ? "touch-none text-neutral-300" : "")
                                }
                                spellCheck="false"
                                // disabled={isPriceFetching}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    {hasError ? (
                        <div
                            className={`mb-2 flex translate-y-0 transform items-center gap-2 opacity-100 transition-all duration-500`}
                        >
                            <Info width="12" height="12" className="text-error-600" />
                            <p className="text-sm text-error-600">
                                {Number(priceValue) >
                                Number(SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY].MAX_AMT)
                                    ? `Maximum amount should be ${
                                          SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY]
                                              .MAX_AMT
                                      }`
                                    : `Minimum amount should be ${
                                          SIP[activeTab.toUpperCase() as BULLION_SIP_TYPE_KEY]
                                              .MIN_AMT
                                      }`}
                            </p>
                        </div>
                    ) : null}

                    <BullionCarousel slides={SLIDES} options={OPTIONS} />

                    {/* <p
                        id="info-container"
                        className="mt-s7 flex items-center gap-2 text-left text-sm leading-[160%] text-neutral-500 md:mt-0 md:text-base"
                    >
                        <button id="info-btn" className="group relative">
                            <Info
                                id="info-icon"
                                size={15}
                                className={`cursor-pointer text-black`}
                            />
                            <div
                                id="info-text"
                                className="absolute left-0 top-full mt-2 hidden w-56 rounded-md bg-black p-1 text-xs text-white group-hover:block sm:left-0 sm:mx-0 md:left-0 md:w-80"
                            >
                                The market average returns for {type?.toLowerCase()} are 11%
                                annually.
                            </div>
                        </button>
                        Start investing with as little as Rs.10/-
                    </p> */}
                </div>
                {/* <div
                    id="block-total-gained"
                    className="mb-3 flex items-center justify-between md:mb-s9"
                >
                    <p id="name" className="text-sm font-semibold leading-[160%] md:text-base">
                        Total Gained
                    </p>
                    <p id="value" className="text-sm font-semibold leading-[160%] md:text-base">
                        {formatIndianCurrency(21500)}
                    </p>
                </div> */}
                {/* <div id="block-total-grams" className="flex items-center justify-between">
                    <p
                        id="name"
                        className="flex items-center text-sm font-semibold leading-[160%] md:text-base"
                    >
                        Total Grams&nbsp; */}
                {/* <button id="info-btn" className="group relative">
                            <Info
                                id="info-icon"
                                size={15}
                                className={`cursor-pointer text-black`}
                            />
                            <div
                                id="info-text"
                                className="absolute left-0 top-full mt-2 hidden min-w-s25 rounded-md bg-black p-1 text-xs text-white group-hover:block sm:left-0 sm:mx-0 md:left-0"
                            >
                                @Marshal to provide text.
                            </div>
                        </button> */}
                {/* </p>
                    <p id="value" className="text-sm font-semibold leading-[160%] md:text-base">
                        {displayQuantity}
                    </p>
                </div> */}
            </div>
        </>
    );
});

export default SIPCalc;
